/**
 * Toasts — la confirmation discrète d'une action réussie (2026-10-14, bloc 1).
 *
 * Un bandeau qui reste finit par ne plus être lu, et le jour où il devient
 * rouge personne ne le voit. Les succès passent donc ici : trois secondes en
 * bas à droite, puis plus rien. Les PROBLÈMES restent des bandeaux : eux
 * doivent être lus.
 *
 * Aucun contexte, aucun hook à brancher : `toast('Enregistré')` depuis
 * n'importe où, un seul `<Toaster />` dans `App`. Les entrées et sorties sont
 * des transitions CSS (interruptibles), pas des keyframes : deux toasts qui
 * s'enchaînent ne se marchent pas dessus.
 */

import { useEffect, useState } from 'react';

interface ToastItem {
  id: number;
  message: string;
  /** `true` pendant la sortie : l'élément reste dans le DOM le temps du fondu. */
  leaving: boolean;
}

const DURATION_MS = 3000;
const EXIT_MS = 160;

type Listener = (items: ToastItem[]) => void;
let items: ToastItem[] = [];
let nextId = 1;
const listeners = new Set<Listener>();

function publish(): void {
  for (const listener of listeners) listener(items);
}

export function toast(message: string): void {
  const id = nextId++;
  items = [...items, { id, message, leaving: false }];
  publish();
  setTimeout(() => {
    items = items.map((t) => (t.id === id ? { ...t, leaving: true } : t));
    publish();
    setTimeout(() => {
      items = items.filter((t) => t.id !== id);
      publish();
    }, EXIT_MS);
  }, DURATION_MS);
}

export function Toaster(): React.ReactElement | null {
  const [list, setList] = useState<ToastItem[]>(items);
  useEffect(() => {
    listeners.add(setList);
    return () => {
      listeners.delete(setList);
    };
  }, []);
  if (list.length === 0) return null;
  return (
    <div className="toaster" role="status" aria-live="polite">
      {list.map((t) => (
        <div key={t.id} className={`toast${t.leaving ? ' is-leaving' : ''}`}>
          <span className="toast-mark" aria-hidden="true">
            ✓
          </span>
          {t.message}
        </div>
      ))}
    </div>
  );
}
